import { useCallback, useEffect, useMemo, useState, type CSSProperties, type PointerEvent as ReactPointerEvent, type RefObject } from "react";
import { applyCanvasLayout, canvasDefaultLayout, canvasFontOptions, responsiveCanvasFactor, responsiveCanvasLayout, sanitizeCanvasLayout } from "../canvas-layout";
import type { CanvasElementLayout, CanvasFontFamily } from "../../types/wedding";

export interface CanvasSelection {
  id: string;
  element: HTMLElement;
}

interface Props {
  rootRef: RefObject<HTMLDivElement | null>;
  selection: CanvasSelection | null;
  layouts: Record<string, CanvasElementLayout>;
  onSelectionChange: (selection: CanvasSelection | null) => void;
  onCommit: (elementId: string, layout: CanvasElementLayout | null) => void;
  editableField?: string;
  editableValue?: string;
  inlineEditRequest?: number;
  onTextCommit?: (field: string, value: string) => void;
  editorZoom?: number;
}

interface SelectionBox { left: number; top: number; width: number; height: number }

function beginInlineTextEditing({ element, field, value, onCommit, onFinish, onResize }: {
  element: HTMLElement;
  field: string;
  value?: string;
  onCommit: (field: string, value: string) => void;
  onFinish: () => void;
  onResize: () => void;
}) {
  const renderedValue = element.innerText || element.textContent || "";
  let finished = false;
  element.textContent = value ?? renderedValue;
  element.setAttribute("contenteditable", "plaintext-only");
  element.setAttribute("role", "textbox");
  element.setAttribute("aria-label", `Edit ${field}`);
  element.classList.add("canvas-inline-editing");

  const finish = (save: boolean) => {
    if (finished) return;
    finished = true;
    const nextValue = (element.innerText || element.textContent || "").replace(/\u00a0/g, " ").trim();
    element.removeAttribute("contenteditable");
    element.removeAttribute("role");
    element.removeAttribute("aria-label");
    element.classList.remove("canvas-inline-editing");
    if (save && nextValue && nextValue !== value) onCommit(field, nextValue);
    else if (!save) element.textContent = renderedValue;
    onFinish();
  };
  const onBlur = () => finish(true);
  const onInput = () => window.requestAnimationFrame(onResize);
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Escape") { event.preventDefault(); finish(false); }
    if (event.key === "Enter" && (event.ctrlKey || event.metaKey || !["P", "BLOCKQUOTE"].includes(element.tagName))) {
      event.preventDefault();
      finish(true);
    }
  };
  element.addEventListener("blur", onBlur);
  element.addEventListener("input", onInput);
  element.addEventListener("keydown", onKeyDown);
  window.requestAnimationFrame(() => {
    element.focus();
    const range = document.createRange();
    range.selectNodeContents(element);
    const selectionRange = window.getSelection();
    selectionRange?.removeAllRanges();
    selectionRange?.addRange(range);
  });
  return () => {
    element.removeEventListener("blur", onBlur);
    element.removeEventListener("input", onInput);
    element.removeEventListener("keydown", onKeyDown);
    element.removeAttribute("contenteditable");
    element.removeAttribute("role");
    element.removeAttribute("aria-label");
    element.classList.remove("canvas-inline-editing");
    if (!finished) element.textContent = renderedValue;
  };
}

const viewportWidth = () => document.documentElement.clientWidth || window.innerWidth;

export function CanvasLayoutEditor({ rootRef, selection, layouts, onSelectionChange, onCommit, editableField, editableValue, inlineEditRequest = 0, onTextCommit, editorZoom = 100 }: Props) {
  const [box, setBox] = useState<SelectionBox | null>(null);
  const [draft, setDraft] = useState<CanvasElementLayout>(canvasDefaultLayout);
  const [editingText, setEditingText] = useState(false);

  const updateBox = useCallback(() => {
    if (!selection?.element.isConnected) return setBox(null);
    const rect = selection.element.getBoundingClientRect();
    setBox({ left: rect.left, top: rect.top, width: rect.width, height: rect.height });
  }, [selection]);

  useEffect(() => {
    if (!selection) { setBox(null); return; }
    const current = sanitizeCanvasLayout(layouts[selection.id]);
    setDraft(current);
    applyCanvasLayout(selection.element, layouts[selection.id], viewportWidth());
    updateBox();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(updateBox);
    observer?.observe(selection.element);
    window.addEventListener("resize", updateBox);
    window.addEventListener("scroll", updateBox, true);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", updateBox);
      window.removeEventListener("scroll", updateBox, true);
    };
  }, [layouts, selection, updateBox]);

  useEffect(() => {
    if (!selection) return;
    const replacement = Array.from(rootRef.current?.querySelectorAll<HTMLElement>("[data-canvas-id]") || []).find((element) => element.dataset.canvasId === selection.id);
    if (replacement && replacement !== selection.element) onSelectionChange({ ...selection, element: replacement });
  }, [rootRef, selection, onSelectionChange]);

  useEffect(() => {
    if (inlineEditRequest > 0 && editableField && onTextCommit) setEditingText(true);
  }, [editableField, inlineEditRequest, onTextCommit]);

  useEffect(() => {
    if (!editingText || !selection || !editableField || !onTextCommit) return;
    return beginInlineTextEditing({ element: selection.element, field: editableField, value: editableValue, onCommit: onTextCommit, onFinish: () => setEditingText(false), onResize: updateBox });
  }, [editableField, editableValue, editingText, onTextCommit, selection, updateBox]);

  useEffect(() => {
    if (!selection) return;
    const keyboard = (event: KeyboardEvent) => {
      if (event.key === "Escape") { onSelectionChange(null); return; }
      const active = document.activeElement;
      if (active instanceof HTMLInputElement || active instanceof HTMLSelectElement || active instanceof HTMLTextAreaElement || (active instanceof HTMLElement && active.isContentEditable)) return;
      const amount = event.shiftKey ? 1.5 : 0.35;
      const direction = event.key === "ArrowLeft" ? [-amount, 0] : event.key === "ArrowRight" ? [amount, 0] : event.key === "ArrowUp" ? [0, -amount] : event.key === "ArrowDown" ? [0, amount] : null;
      if (!direction) return;
      event.preventDefault();
      const next = sanitizeCanvasLayout({ ...draft, x: draft.x + direction[0], y: draft.y + direction[1] });
      setDraft(next);
      applyCanvasLayout(selection.element, next, viewportWidth());
      onCommit(selection.id, next);
      window.requestAnimationFrame(updateBox);
    };
    window.addEventListener("keydown", keyboard);
    return () => window.removeEventListener("keydown", keyboard);
  }, [draft, onCommit, onSelectionChange, selection, updateBox]);

  const startGesture = (event: ReactPointerEvent, kind: "move" | "scale" | "width", xDirection = 1, yDirection = 1) => {
    if (!selection || !box) return;
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    const pointerId = event.pointerId;
    const startX = event.clientX;
    const startY = event.clientY;
    const start = draft;
    const currentViewportWidth = viewportWidth();
    const factor = responsiveCanvasFactor(currentViewportWidth);
    const displayedStart = responsiveCanvasLayout(start, currentViewportWidth);
    let next = start;

    const move = (moveEvent: PointerEvent) => {
      if (moveEvent.pointerId !== pointerId) return;
      if (kind === "move") {
        next = sanitizeCanvasLayout({
          ...start,
          x: start.x + ((moveEvent.clientX - startX) / currentViewportWidth * 100) / factor,
          y: start.y + ((moveEvent.clientY - startY) / window.innerHeight * 100) / factor
        });
      } else if (kind === "width") {
        const visualWidth = Math.max(48, box.width + (moveEvent.clientX - startX) * xDirection);
        const unscaledWidth = visualWidth / Math.max(0.5, displayedStart.scale);
        next = sanitizeCanvasLayout({ ...start, width: unscaledWidth / currentViewportWidth * 100 });
      } else {
        const distance = ((moveEvent.clientX - startX) * xDirection + (moveEvent.clientY - startY) * yDirection) / Math.max(80, box.width + box.height) * 2;
        const displayedScale = Math.min(2.5, Math.max(0.5, displayedStart.scale + distance));
        next = sanitizeCanvasLayout({ ...start, scale: 1 + (displayedScale - 1) / factor });
      }
      setDraft(next);
      applyCanvasLayout(selection.element, next, currentViewportWidth);
      window.requestAnimationFrame(updateBox);
    };
    const end = (upEvent: PointerEvent) => {
      if (upEvent.pointerId !== pointerId) return;
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", end);
      window.removeEventListener("pointercancel", end);
      if (JSON.stringify(next) !== JSON.stringify(start)) onCommit(selection.id, next);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", end);
    window.addEventListener("pointercancel", end);
  };

  const toolbarStyle = useMemo(() => {
    if (!box) return undefined;
    const inverseScale = 100 / Math.max(30, editorZoom);
    const width = Math.min(380, window.innerWidth - 12);
    const left = Math.min(window.innerWidth - width / 2 - 8, Math.max(width / 2 + 8, box.left + box.width / 2));
    const toolbarOffset = 64 * inverseScale;
    const top = box.top > toolbarOffset + 8 ? box.top - toolbarOffset : Math.min(window.innerHeight - toolbarOffset, box.top + box.height + 10 * inverseScale);
    return { left, top, width, "--canvas-ui-scale": String(inverseScale), transform: `translateX(-50%) scale(${inverseScale})` } as CSSProperties;
  }, [box, editorZoom]);

  if (!selection || !box || !toolbarStyle) return null;
  const label = selection.element.getAttribute("alt") || selection.element.textContent?.trim().replace(/\s+/g, " ") || selection.element.tagName.toLowerCase();

  const commit = (next: CanvasElementLayout) => {
    const safe = sanitizeCanvasLayout(next);
    setDraft(safe);
    applyCanvasLayout(selection.element, safe, viewportWidth());
    onCommit(selection.id, safe);
    window.requestAnimationFrame(updateBox);
  };

  return <div data-canvas-ui="true">
    <div className="canvas-selection-box" style={{ left: box.left, top: box.top, width: box.width, height: box.height, "--canvas-ui-scale": String(100 / Math.max(30, editorZoom)) } as CSSProperties}>
      {!editingText ? <button className="canvas-drag-surface" type="button" aria-label={`Move ${label}`} title={editableField ? "Drag to move · double-click to edit text" : "Drag to move"} onDoubleClick={() => { if (editableField && onTextCommit) setEditingText(true); }} onPointerDown={(event) => startGesture(event, "move")} /> : null}
      {!editingText ? <>
        <button className="canvas-resize-handle top-left" type="button" aria-label="Resize from top left" onPointerDown={(event) => startGesture(event, "scale", -1, -1)} />
        <button className="canvas-resize-handle top-right" type="button" aria-label="Resize from top right" onPointerDown={(event) => startGesture(event, "scale", 1, -1)} />
        <button className="canvas-resize-handle bottom-left" type="button" aria-label="Resize from bottom left" onPointerDown={(event) => startGesture(event, "scale", -1, 1)} />
        <button className="canvas-resize-handle bottom-right" type="button" aria-label="Resize from bottom right" onPointerDown={(event) => startGesture(event, "scale", 1, 1)} />
        <button className="canvas-edge-handle top" type="button" aria-label="Resize from top" onPointerDown={(event) => startGesture(event, "scale", 0, -1)} />
        <button className="canvas-edge-handle right" type="button" aria-label="Resize width from right" onPointerDown={(event) => startGesture(event, "width", 1, 0)} />
        <button className="canvas-edge-handle bottom" type="button" aria-label="Resize from bottom" onPointerDown={(event) => startGesture(event, "scale", 0, 1)} />
        <button className="canvas-edge-handle left" type="button" aria-label="Resize width from left" onPointerDown={(event) => startGesture(event, "width", -1, 0)} />
      </> : null}
    </div>
    <div className="canvas-toolbar" style={toolbarStyle} role="toolbar" aria-label={`Edit ${label}`} onPointerDown={(event) => event.stopPropagation()}>
      <span className="canvas-toolbar-label" title="Drag the selected content">✥</span>
      {editableField ? <button className="canvas-edit-text" type="button" aria-label="Edit text" title="Double-click text to edit" onClick={() => setEditingText(true)}>✎</button> : null}
      <label>Font<select aria-label="Font family" value={draft.fontFamily} onChange={(event) => commit({ ...draft, fontFamily: event.target.value as CanvasFontFamily })}>{canvasFontOptions.map((option) => <option value={option.value} key={option.value}>{option.label}</option>)}</select></label>
      <div className="canvas-scale-control"><button type="button" aria-label="Make smaller" onClick={() => commit({ ...draft, scale: draft.scale - 0.1 })}>−</button><output>{Math.round(draft.scale * 100)}%</output><button type="button" aria-label="Make bigger" onClick={() => commit({ ...draft, scale: draft.scale + 0.1 })}>+</button></div>
      <button className="canvas-reset" type="button" onClick={() => { applyCanvasLayout(selection.element, undefined, viewportWidth()); setDraft(canvasDefaultLayout); onCommit(selection.id, null); window.requestAnimationFrame(updateBox); }}>Reset</button>
    </div>
  </div>;
}
